import {
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterDocumentTagMatchMode,
} from '@griply/types';
import React from 'react';
import { Option } from 'shared/components/ui/custom-form-select';
import { ButtonVariants } from 'shared/components/ui/popup-menu';

import { DateFormatOptions } from './date-format-options';
import { ID } from './id';
import { WeekDays } from './week-days';

export type TagMatchModeOption = {
  value: FilterDocumentTagMatchMode;
  label: string;
  description: string;
};

export enum FilterOptionType {
  Time = 'time',
  Date = 'date',
  DateRange = 'date-range',
  Select = 'select',
  MultiSelect = 'multi-select',
  Checkbox = 'checkbox',
  NumberRange = 'number-range',
  Button = 'button',
}

export type FilterOptionBase = {
  id: ID;
  label: string;
  tooltip?: string;
  children?: FilterGroup[];
  canReset?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  infoUrl?: string;
};

export type FilterOptionDefault = FilterOptionBase & {
  type?: Exclude<
    FilterOptionType,
    | FilterOptionType.Select
    | FilterOptionType.MultiSelect
    | FilterOptionType.Checkbox
    | FilterOptionType.NumberRange
    | FilterOptionType.DateRange
    | FilterOptionType.Button
  >;
};

export type FilterSelectOption = FilterOptionBase & {
  type: FilterOptionType.Select | FilterOptionType.MultiSelect;
  selectOptions: Option[];
  placeholder: string;
  recursiveSelection?: boolean;
  enableSearch?: boolean;
  optionsAsButtons?: boolean;
  noOptionsLabel?: string;
  tagMatchMode?: FilterDocumentTagMatchMode;
  tagMatchModeOptions?: TagMatchModeOption[];
};

export type FilterCheckboxOption = Omit<FilterOptionBase, 'icon'> & {
  type: FilterOptionType.Checkbox;
};

export type FilterNumberRangeOption = FilterOptionBase & {
  type: FilterOptionType.NumberRange;
  minLabel: string;
  maxLabel: string;
  placeholder: string;
};

export type FilterDateRangeOption = FilterOptionBase & {
  type: FilterOptionType.DateRange;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  isDeadline?: boolean;
  hideUndefinedDateOptions?: boolean;
  hideFuturePresets?: boolean;
};

export type FilterButtonOption = FilterOptionBase & {
  type: FilterOptionType.Button;
  buttonVariant?: ButtonVariants;
  disabled?: boolean;
};

export type FilterOption =
  | FilterOptionDefault
  | FilterSelectOption
  | FilterCheckboxOption
  | FilterNumberRangeOption
  | FilterDateRangeOption
  | FilterButtonOption;

export type FilterGroup = {
  id: ID;
  title?: string;
  filters: FilterOption[];
  scrollable?: boolean;
};

export type ActiveFilter = {
  id: ID;
  value: string[];
  dateFilter?: FilterDocumentDateFilter;
  durationFilter?: FilterDocumentDurationFilter;
  isDefault?: boolean;
};
